import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { motion, AnimatePresence } from 'framer-motion';

const SERVICE_ID = 'service_xya8i69';
const TEMPLATE_ID = 'template_jo5mirs';
const PUBLIC_KEY = 'gR2vt_ANCNyTSiOfV';

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', msg: '' });
  
  const [formData, setFormData] = useState({
    user_name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', msg: '' });

    try {
      const response = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        formData, 
        PUBLIC_KEY
      );

      if (response.status === 200) {
        setStatus({ type: 'success', msg: '✅ Message Sent Successfully!' });
        setFormData({ user_name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error("EmailJS Error:", error);
      setStatus({ 
        type: 'error', 
        msg: `❌ Error: ${error?.text || 'Check Connection'}` 
      });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setStatus({ type: '', msg: '' }), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-blue-600 selection:text-white">
      
      <AnimatePresence>
        {status.msg && (
          <motion.div 
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-5 md:top-10 left-1/2 -translate-x-1/2 z-[100] w-[90%] md:w-auto px-6 md:px-8 py-4 rounded-full shadow-2xl border font-bold text-xs md:text-sm tracking-widest text-center ${
              status.type === 'success' ? 'bg-white border-green-500 text-green-700' : 'bg-white border-red-500 text-red-700'
            }`}
          >
            {status.msg}
          </motion.div>
        )}
      </AnimatePresence>

      <section className="pt-10 md:pt-20 pb-12 md:pb-16 px-4 md:px-6 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto text-center lg:text-left">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-slate-900 tracking-tighter leading-[1.1] md:leading-none uppercase"
          >
            Let's <span className="text-blue-600 italic">Connect.</span>
          </motion.h1>
          <p className="mt-4 md:mt-6 text-slate-500 text-base md:text-lg lg:text-xl font-medium max-w-2xl mx-auto lg:mx-0">
            Partner with PC WORLD for enterprise-grade IT solutions and dedicated B2B support in Central India.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
          
          <div className="lg:col-span-2 space-y-4 md:space-y-8">
            {/* CLICKABLE ADDRESS */}
            <a 
              href="https://www.google.com/maps/search/?api=1&query=PC+WORLD+Nagdev+Plaza+Jail+Rd+Raipur" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block group"
            >
              <div className="bg-white p-6 md:p-8 rounded-[2rem] md:rounded-[2.5rem] shadow-sm border border-slate-100 group-hover:border-blue-200 group-hover:shadow-xl transition-all">
                <div className="flex flex-col sm:flex-row gap-4 md:gap-6 items-start sm:items-center lg:items-start">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center text-xl md:text-2xl flex-shrink-0">📍</div>
                  <div>
                    <h4 className="font-black text-slate-400 uppercase tracking-widest text-[9px] md:text-[10px] mb-1 md:mb-2">Main Office</h4>
                    <p className="text-slate-900 font-bold text-base md:text-lg leading-snug">
                      Shop No.5 Ground Floor Nagdev Plaza, Jail Rd, Raipur, CG 492001
                    </p>
                  </div>
                </div>
              </div>
            </a>

            {/* CLICKABLE PHONE */}
            <a href="tel:+919302707699" className="block group">
              <div className="bg-white p-6 md:p-8 rounded-[2rem] md:rounded-[2.5rem] shadow-sm border border-slate-100 group-hover:border-slate-900 group-hover:shadow-xl transition-all">
                <div className="flex flex-col sm:flex-row gap-4 md:gap-6 items-start sm:items-center lg:items-start">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-slate-900 text-white rounded-2xl flex items-center justify-center text-xl md:text-2xl flex-shrink-0">📞</div>
                  <div>
                    <h4 className="font-black text-slate-400 uppercase tracking-widest text-[9px] md:text-[10px] mb-1 md:mb-2">Direct Line</h4>
                    <p className="text-slate-900 font-bold text-lg md:text-xl tracking-tight">+91 93027 07699</p>
                  </div>
                </div>
              </div>
            </a>

            {/* CLICKABLE EMAIL */}
            <a href="mailto:salesraipur@pcworldraipur.in" className="block group">
              <div className="bg-white p-6 md:p-8 rounded-[2rem] md:rounded-[2.5rem] shadow-sm border border-slate-100 group-hover:border-blue-600 group-hover:shadow-xl transition-all">
                <div className="flex flex-col sm:flex-row gap-4 md:gap-6 items-start sm:items-center lg:items-start">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-600 text-white rounded-2xl flex items-center justify-center text-xl md:text-2xl flex-shrink-0">✉️</div>
                  <div>
                    <h4 className="font-black text-slate-400 uppercase tracking-widest text-[9px] md:text-[10px] mb-1 md:mb-2">Official Email</h4>
                    <p className="text-slate-900 font-bold text-base md:text-lg lowercase truncate max-w-[220px] sm:max-w-none">salesraipur@pcworldraipur.in</p>
                  </div>
                </div>
              </div>
            </a>
          </div>

          <div className="lg:col-span-3">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-slate-900 p-6 sm:p-8 lg:p-14 rounded-[2.5rem] md:rounded-[3.5rem] shadow-2xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 blur-[100px] -z-10" />
              
              <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                  <div className="space-y-2">
                    <label className="text-[9px] md:text-[10px] font-black uppercase text-blue-400 ml-2 tracking-widest">Full Name</label>
                    <input 
                      type="text" name="user_name" value={formData.user_name} onChange={handleChange} required 
                      placeholder="Your Name" 
                      className="w-full bg-white/5 border border-white/10 rounded-xl md:rounded-2xl px-5 py-3 md:px-6 md:py-4 text-white focus:border-blue-500 outline-none transition-all placeholder:text-slate-600" 
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] md:text-[10px] font-black uppercase text-blue-400 ml-2 tracking-widest">Email Address</label>
                    <input 
                      type="email" name="email" value={formData.email} onChange={handleChange} required 
                      placeholder="business@email.com" 
                      className="w-full bg-white/5 border border-white/10 rounded-xl md:rounded-2xl px-5 py-3 md:px-6 md:py-4 text-white focus:border-blue-500 outline-none transition-all placeholder:text-slate-600" 
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[9px] md:text-[10px] font-black uppercase text-blue-400 ml-2 tracking-widest">Contact Number</label>
                  <input 
                    type="tel" name="phone" value={formData.phone} onChange={handleChange} 
                    placeholder="+91 00000 00000" 
                    className="w-full bg-white/5 border border-white/10 rounded-xl md:rounded-2xl px-5 py-3 md:px-6 md:py-4 text-white focus:border-blue-500 outline-none transition-all placeholder:text-slate-600" 
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[9px] md:text-[10px] font-black uppercase text-blue-400 ml-2 tracking-widest">Inquiry Details</label>
                  <textarea 
                    name="message" value={formData.message} onChange={handleChange} required rows="4" 
                    placeholder="Tell us about your IT requirements..." 
                    className="w-full bg-white/5 border border-white/10 rounded-xl md:rounded-2xl px-5 py-3 md:px-6 md:py-4 text-white focus:border-blue-500 outline-none transition-all resize-none placeholder:text-slate-600"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-white hover:text-blue-600 text-white py-4 md:py-6 rounded-xl md:rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] md:text-xs transition-all shadow-xl active:scale-95 disabled:opacity-50"
                >
                  {isSubmitting ? "PROCESSING..." : "SUBMIT MESSAGE"}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GOOGLE MAPS - Corrected with actual Raipur Location embed */}
      <section className="px-4 md:px-6 pb-12 md:pb-20">
        <div className="max-w-7xl mx-auto h-[350px] md:h-[500px] rounded-[2rem] md:rounded-[3.5rem] overflow-hidden shadow-2xl border-4 md:border-8 border-white group relative">
          
           
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3718.6031296224755!2d81.63698687638933!3d21.24758063006685!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a28dd4364250373%3A0x7b05e4b94d2a55a1!2sPC%20WORLD%20%7CComputer%20Shop%20In%20Raipur!5e0!3m2!1sen!2sin!4v1767778243526!5m2!1sen!2sin" 
            className="w-full h-full border-0 grayscale hover:grayscale-0 transition-all duration-700"
            allowFullScreen="" 
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="PC World Raipur Location"
          ></iframe>
          {/* Overlay Link for better mobile map navigation */}
          <a 
            href="https://maps.app.goo.gl/tMj5JLsLVpSpzJGQ7" 
            target="_blank" 
            rel="noopener noreferrer"
            className="absolute top-4 right-4 bg-white/90 backdrop-blur text-slate-900 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg md:opacity-0 group-hover:opacity-100 transition-opacity"
          >
            Open in Maps ↗
          </a>
        </div>
      </section>
    </div>
  );
}